import { useState } from 'react';
import betClient from '../../api/betClient';

const SPORT_LABELS = {
  americanfootball_nfl: 'NFL',
  americanfootball_ncaaf: 'NCAAF',
  basketball_nba: 'NBA',
  basketball_ncaab: 'NCAAB',
  baseball_mlb: 'MLB',
  icehockey_nhl: 'NHL',
  soccer_usa_mls: 'MLS',
  mma_mixed_martial_arts: 'MMA',
  boxing_boxing: 'Boxing',
};

function sportLabel(key) {
  return SPORT_LABELS[key] || key;
}

function SectionHeader({ children }) {
  return (
    <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E5E7EB] dark:border-[#1e2536]">
      <div className="w-[3px] h-4 bg-[#005BB3] rounded-full" />
      <h3 className="text-[12px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] dark:text-[#8892a8]">
        {children}
      </h3>
    </div>
  );
}

function ResultsTable({ columns, rows }) {
  return (
    <table className="w-full text-[12px]" style={{ fontFamily: 'var(--font-mono)' }}>
      <thead>
        <tr className="border-b border-[#E5E7EB] dark:border-[#1e2536]">
          {columns.map((col) => (
            <th
              key={col.key}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.5px] text-[#9CA3AF] dark:text-[#505a6e] font-medium ${col.right ? 'text-right' : 'text-left'}`}
            >
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(({ key, cells, status, errorMsg }) => (
          <tr key={key} className="border-b border-[#F3F4F6] dark:border-[#1a2030] last:border-0">
            {cells.map((cell, i) => (
              <td
                key={i}
                className={`px-4 py-2 ${columns[i].right ? 'text-right' : ''} ${cell.className || 'text-[#111827] dark:text-[#e2e8f0]'}`}
              >
                {cell.value}
              </td>
            ))}
            <td className="px-4 py-2 text-right">
              {status === 'error' && (
                <div className="flex flex-col items-end gap-0.5">
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[rgba(220,38,38,0.1)] text-[#DC2626]">ERROR</span>
                  {errorMsg && (
                    <span className="text-[10px] text-[#DC2626] opacity-60 max-w-[200px] truncate" title={errorMsg}>{errorMsg}</span>
                  )}
                </div>
              )}
              {status === 'dry_run' && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[rgba(107,114,128,0.1)] text-[#6B7280]">DRY RUN</span>
              )}
              {status === 'skipped' && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[rgba(248,106,56,0.1)] text-[#F86A38]">SKIPPED</span>
              )}
              {status === 'ok' && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[rgba(2,148,86,0.1)] text-[#029456]">OK</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function ManualControls() {
  // status: idle | confirming | running | done | error
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  function handleTriggerClick() {
    setStatus('confirming');
  }

  function handleCancel() {
    setStatus('idle');
  }

  async function handleConfirm() {
    setStatus('running');
    setResult(null);
    setErrorMsg(null);
    try {
      const res = await betClient.post(
        '/api/historical/cron/force-daily-generation?dryRun=false',
      );
      setResult(res.data);
      setStatus('done');
    } catch (e) {
      setErrorMsg(
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        'Unknown error',
      );
      setStatus('error');
    }
  }

  function handleReset() {
    setStatus('idle');
    setResult(null);
    setErrorMsg(null);
  }

  // Build prediction rows from result
  const predSports = result?.predictions?.sports
    ? Object.entries(result.predictions.sports)
    : [];
  const predSkipped = result?.predictions?.skipped;
  const predSkipReason = result?.predictions?.reason;

  const propSports = result?.prop_picks?.sports
    ? Object.entries(result.prop_picks.sports)
    : [];
  const propSkipped = result?.prop_picks?.skipped;
  const propSkipReason = result?.prop_picks?.reason;

  const triggeredAt = result?.triggered_at
    ? new Date(result.triggered_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : null;

  return (
    <div className="px-5 py-6" style={{ fontFamily: 'var(--font-sans)' }}>
      <div style={{ maxWidth: 1100 }}>
        {/* Page header */}
        <div className="mb-5">
          <h1 className="text-[22px] font-semibold text-[#111827] dark:text-[#e2e8f0] mb-1">
            Manual Controls
          </h1>
          <p className="text-[13px] text-[#6B7280] dark:text-[#8892a8]">
            Admin tools for triggering generation tasks outside their scheduled windows.
          </p>
        </div>

        {/* Force generation card */}
        <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] mb-4">
          <SectionHeader>Force Daily Generation</SectionHeader>
          <div className="px-4 py-4">
            <p className="text-[13px] text-[#4B5563] dark:text-[#8892a8] mb-4 max-w-[600px]">
              Bypasses the 12:00–14:00 UTC generation window and runs daily predictions and
              prop picks immediately. Use after API key rotations, Railway outages, or missed
              windows. Per-event deduplication still applies — safe to call if predictions
              partially exist.
            </p>

            {status === 'idle' && (
              <button
                onClick={handleTriggerClick}
                className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#005BB3] hover:bg-[#004a99] text-white text-[13px] font-semibold transition-colors duration-150"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                </svg>
                Force Generate Now
              </button>
            )}

            {status === 'running' && (
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-[#005BB3] animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-[13px] text-[#6B7280] dark:text-[#8892a8]">
                  Generating predictions and prop picks — this takes 30–60 seconds…
                </span>
              </div>
            )}

            {(status === 'done' || status === 'error') && (
              <button
                onClick={handleReset}
                className="px-3 py-1.5 rounded border border-[#E5E7EB] dark:border-[#1e2536] text-[12px] text-[#6B7280] dark:text-[#8892a8] hover:bg-[#F8F9FA] dark:hover:bg-[#1a2030] transition-colors duration-150"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Error banner */}
        {status === 'error' && errorMsg && (
          <div className="rounded bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] border border-[#fecaca] dark:border-[#7f1d1d] px-3 py-2.5 mb-4">
            <p className="text-[#ef4444] text-[12px] font-semibold mb-0.5">Generation failed</p>
            <p className="text-[#ef4444] text-[11px]">{errorMsg}</p>
          </div>
        )}

        {/* Results */}
        {status === 'done' && result && (
          <>
            {/* Success banner */}
            <div className="rounded bg-[rgba(2,148,86,0.06)] dark:bg-[rgba(2,148,86,0.08)] border border-[rgba(2,148,86,0.2)] dark:border-[rgba(2,148,86,0.25)] px-3 py-2.5 mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-[#029456] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-[#029456] text-[12px] font-semibold">
                Generation complete{triggeredAt ? ` at ${triggeredAt}` : ''}
              </span>
            </div>

            {/* Predictions table */}
            <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] mb-4">
              <SectionHeader>Predictions</SectionHeader>
              {predSkipped ? (
                <p className="px-4 py-3 text-[12px] text-[#6B7280] dark:text-[#8892a8]">
                  Skipped — {predSkipReason?.replace(/_/g, ' ') ?? 'unknown reason'}
                </p>
              ) : predSports.length === 0 ? (
                <p className="px-4 py-3 text-[12px] text-[#6B7280] dark:text-[#8892a8]">No data returned</p>
              ) : (
                <ResultsTable
                  columns={[
                    { key: 'sport', label: 'Sport' },
                    { key: 'stored', label: 'Stored', right: true },
                    { key: 'skipped', label: 'Skipped', right: true },
                    { key: 'status', label: 'Status', right: true },
                  ]}
                  rows={predSports.map(([key, val]) => ({
                    key,
                    cells: [
                      { value: sportLabel(key) },
                      { value: val.stored ?? '—', className: 'text-right text-[#029456] font-semibold' },
                      { value: val.skipped ?? '—', className: 'text-right text-[#6B7280] dark:text-[#8892a8]' },
                    ],
                    status: val.error ? 'error' : val.dry_run ? 'dry_run' : 'ok',
                    errorMsg: val.error || null,
                  }))}
                />
              )}
            </div>

            {/* Prop picks table */}
            <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25]">
              <SectionHeader>Prop Picks</SectionHeader>
              {propSkipped ? (
                <p className="px-4 py-3 text-[12px] text-[#6B7280] dark:text-[#8892a8]">
                  Skipped — {propSkipReason?.replace(/_/g, ' ') ?? 'unknown reason'}
                </p>
              ) : propSports.length === 0 ? (
                <p className="px-4 py-3 text-[12px] text-[#6B7280] dark:text-[#8892a8]">No data returned</p>
              ) : (
                <ResultsTable
                  columns={[
                    { key: 'sport', label: 'Sport' },
                    { key: 'stored', label: 'Stored', right: true },
                    { key: 'games', label: 'Games', right: true },
                    { key: 'status', label: 'Status', right: true },
                  ]}
                  rows={propSports.map(([key, val]) => ({
                    key,
                    cells: [
                      { value: sportLabel(key) },
                      { value: val.stored ?? '—', className: 'text-right text-[#029456] font-semibold' },
                      { value: val.games_analyzed ?? '—', className: 'text-right text-[#6B7280] dark:text-[#8892a8]' },
                    ],
                    status: val.error ? 'error' : val.dry_run ? 'dry_run' : val.reason ? 'skipped' : 'ok',
                    errorMsg: val.error || null,
                  }))}
                />
              )}
            </div>
          </>
        )}
      </div>

      {/* Confirmation modal */}
      {status === 'confirming' && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-[420px] rounded-lg border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] shadow-xl p-5">
            <h2
              className="text-[16px] font-semibold text-[#111827] dark:text-[#e2e8f0] mb-2"
              style={{ fontFamily: 'var(--font-sans)' }}
            >
              Confirm Force Generation
            </h2>
            <p className="text-[13px] text-[#4B5563] dark:text-[#8892a8] mb-4" style={{ fontFamily: 'var(--font-sans)' }}>
              This bypasses the 12:00–14:00 UTC window and triggers daily predictions and
              prop picks immediately.
            </p>

            {/* Cost warning */}
            <div className="rounded border border-[#fed7aa] dark:border-[#78350f] bg-[rgba(248,106,56,0.04)] px-3 py-2.5 mb-5">
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-[#F86A38] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <div>
                  <p className="text-[12px] font-semibold text-[#F86A38] mb-0.5" style={{ fontFamily: 'var(--font-sans)' }}>
                    Estimated API cost
                  </p>
                  <p className="text-[11px] text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>
                    ~7 Odds API credits · ~$0.25 in Anthropic tokens
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={handleCancel}
                className="px-4 py-2 rounded border border-[#E5E7EB] dark:border-[#1e2536] text-[13px] text-[#6B7280] dark:text-[#8892a8] hover:bg-[#F8F9FA] dark:hover:bg-[#1a2030] transition-colors duration-150"
                style={{ fontFamily: 'var(--font-sans)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="px-4 py-2 rounded bg-[#005BB3] hover:bg-[#004a99] text-white text-[13px] font-semibold transition-colors duration-150"
                style={{ fontFamily: 'var(--font-sans)' }}
              >
                Generate Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
